import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * DF-0177 — un sondage qui ralentit quand rien ne change.
 *
 * Mesuré en production le 2026-09-06 : le mode serveur pèse 8,8 % de TOUT le
 * trafic de la plateforme et ~91 % des requêtes reçues par `sale_api`, pour
 * deux tablettes. L'une d'elles a redemandé la MÊME session 5 330 fois en
 * 24 h — une fois toutes les 16 s, sans jamais s'arrêter — et tout répondait
 * 200 : aucun compteur, aucun seuil, aucune alerte ne bouge. La plateforme
 * tient sur un seul processus par service et ne peut pas prendre de second
 * réplica avant redis (DF-0152) : ces requêtes-là sont prélevées sur la
 * capacité qui sert à envoyer les commandes en cuisine.
 *
 * Le sondage n'est pas le chemin nominal — `useWaiterOrderStream` invalide
 * déjà les tags RTK Query sur chaque événement. Il est le filet quand le flux
 * est coupé. Un filet n'a pas besoin de battre à 5 s pendant six heures de
 * service sur une table où rien ne se passe.
 *
 * Règle : à chaque réponse identique à la précédente, l'intervalle double,
 * jusqu'à `max`. La première réponse qui diffère le remet à `base`, et le
 * retour de visibilité de l'onglet aussi — regarder l'écran doit donner une
 * vue fraîche, pas une vue vieille d'une minute.
 *
 * Repose sur le `structuralSharing` de RTK Query (actif par défaut) : une
 * réponse profondément égale conserve la même référence, donc « rien n'a
 * changé » se lit en une comparaison d'identité.
 */
export type PollObserver = (data: unknown, fulfilledTimeStamp: number | undefined) => void;

/**
 * Renvoie `[intervalle, observer]`. L'intervalle se passe tel quel à
 * `pollingInterval` ; `observer` doit ensuite recevoir la réponse de CETTE
 * requête, via `useObservedPollResponse` juste après l'appel — c'est ce
 * couplage en deux temps qui évite la dépendance circulaire entre l'intervalle
 * et la réponse qu'il produit.
 */
export function useAdaptivePolling(
  active: boolean,
  base: number,
  max: number
): [number, PollObserver] {
  const [unchangedStreak, setUnchangedStreak] = useState(0);
  const lastData = useRef<unknown>(undefined);
  const lastStamp = useRef<number | undefined>(undefined);

  const observe = useCallback<PollObserver>(
    (data, fulfilledTimeStamp) => {
      if (!active) {
        // Repartir à `base` au retour : la première vue doit être fraîche.
        lastData.current = undefined;
        lastStamp.current = undefined;
        setUnchangedStreak(0);
        return;
      }
      if (fulfilledTimeStamp === undefined) return;
      if (fulfilledTimeStamp === lastStamp.current) return;

      const isFirstResponse = lastStamp.current === undefined;
      const changed = data !== lastData.current;
      lastStamp.current = fulfilledTimeStamp;
      lastData.current = data;
      setUnchangedStreak((streak) => (isFirstResponse || changed ? 0 : streak + 1));
    },
    [active]
  );

  const pollingInterval = active ? Math.min(base * 2 ** unchangedStreak, max) : 0;
  return [pollingInterval, observe];
}

/** Branche la réponse d'une requête sur l'observateur rendu par `useAdaptivePolling`. */
export function useObservedPollResponse(
  observe: PollObserver,
  data: unknown,
  fulfilledTimeStamp: number | undefined
): void {
  useEffect(() => {
    observe(data, fulfilledTimeStamp);
  }, [observe, data, fulfilledTimeStamp]);
}
